import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import type { DomainBiomassDay } from '@/domain/cages/overview.types';
import { buildGrowthChartSpec } from '@/routes/Client/Cage/overviewCharts';
import { parseLocale } from '@/utils/localeFormatters';

export interface UseGrowthChartSpecParams {
    days: DomainBiomassDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    weightUnitTag: string;
}

/**
 * Builds the growth chart spec, resolving translations and locale.
 *
 * Everything shape-related lives in `buildGrowthChartSpec`; this hook exists only
 * to supply the translated labels and the parsed locale, and to memoise the spec
 * so the chart is not rebuilt on every render.
 */
export function useGrowthChartSpec(params: UseGrowthChartSpecParams): MetricChartSpec<'line'> {
    const { days, graphedDaypartTags, activeDaypartTags, weightUnitTag } = params;
    const { t, i18n } = useTranslation('cageCharts');
    // Daypart names are shared with the bioscope status pie chart, so they stay on
    // their existing root keys.
    const { t: tRoot } = useTranslation();

    return useMemo(
        () =>
            buildGrowthChartSpec({
                days,
                graphedDaypartTags,
                activeDaypartTags,
                daypartLabel: (daypartTag) => tRoot(`daypartTag.${daypartTag}`),
                axisLabel: t('axis.growth'),
                weightUnitTag,
                locale: parseLocale(i18n.language),
            }),
        [days, graphedDaypartTags, activeDaypartTags, weightUnitTag, t, tRoot, i18n.language]
    );
}
